import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Inline,
  Loader,
  Stack,
  Text,
} from 'braid-design-system';
import { AdvertiserNation } from '../../utils/AdvertiserNation';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { usePricingContext } from 'src/App/context/PricingContextProvider';
import { useProductSet } from 'src/hooks';
import { v4 } from 'uuid';
import {
  mapProductSetItems,
  type MappedAdProduct,
} from 'src/mappers/mapProductSetItems';
import { PriceRows } from '../PriceRows/PriceRows';

export interface AdPriceDetailsProps {
  advertiserId?: string;
  roleTitle: string;
  subClassificationId: string;
  locationId: number;
  locationText: string;
  advertiserNation: AdvertiserNation;
  showDiscountedPrice: boolean;
}

export const AdPriceDetails = ({
  advertiserNation,
  roleTitle,
  subClassificationId,
  locationId,
  locationText,
  showDiscountedPrice = true,
}: AdPriceDetailsProps) => {
  const { t } = useTranslations(translations);
  const { appMode, isPreview } = usePricingContext();
  const [priceBlock, setPriceBlock] = useState<MappedAdProduct[]>([]);
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);

  const {
    useProductSetQuery,
    data: productSetData,
    isLoading: productSetLoading,
    error: productSetError,
  } = useProductSet(isPreview);

  useEffect(() => {
    const priceItems: MappedAdProduct[] = mapProductSetItems(
      productSetData?.productSet?.items ?? [],
    );

    const isDiscountedPriceExist = priceItems.some(
      (priceItem) => priceItem.price.discount.discountPercentage > 0,
    );

    const isShowDiscountedPrice =
      showDiscountedPrice && appMode === 'DEFAULT' && isDiscountedPriceExist;

    setPriceBlock(priceItems);
    setShowDiscountInfo(isShowDiscountedPrice);
  }, [productSetData, isPreview]);

  useEffect(() => {
    setShowDiscountInfo(false);
    useProductSetQuery({
      variables: {
        input: {
          advertiserNation,
          jobAdDetails: {
            locationId: locationId.toString(),
            subClassificationId,
            roleTitle,
          },
          opportunityId: v4(),
        },
      },
    });
  }, [locationId, subClassificationId, roleTitle, isPreview]);

  if (productSetLoading) {
    return <Loader delayVisibility={true} />;
  }

  if (productSetError) {
    return (
      <Alert tone="critical">
        <Text>{t('We are currently unable to check prices...')}</Text>
      </Alert>
    );
  }

  return (
    <Stack space="xlarge">
      <Inline space="xsmall" alignY="center">
        <Box paddingTop="xsmall">
          <Text
            id="price_check_title"
            size="large"
            weight="strong"
            component="h3"
          >
            {t('Ad prices for roleTitle in locationText*', {
              roleTitle,
              locationText,
            })}
          </Text>
        </Box>
        {showDiscountInfo && (
          <Box paddingTop="xsmall">
            <Badge
              id="budget_discount_badge"
              data={{ testid: 'budget_discount_badge' }}
              tone="info"
              bleedY={true}
            >
              {t('Budget discounts applied')}
            </Badge>
          </Box>
        )}
      </Inline>
      <PriceRows
        mappedAdProducts={priceBlock}
        showDiscountInfo={showDiscountInfo}
      />
    </Stack>
  );
};
